import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import React, { useState } from "react";
import {
  StyleSheet,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
  type TextStyle,
  View,
  type ViewStyle,
} from "react-native";
import ThemedText from "../core/ThemedText";

type MaskType =
  | "phone"
  | "credit-card"
  | "date"
  | "time"
  | "zip-code"
  | "ssn"
  | "custom";

interface MaskedInputProps extends Omit<RNTextInputProps, "style"> {
  label?: string;
  error?: string;
  maskType?: MaskType;
  customMask?: string; // e.g., "(###) ###-####" where # is a digit
  variant?: "default" | "filled" | "outlined";
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
  disabled?: boolean;
  helperText?: string;
  onChangeUnmasked?: (value: string) => void; // Returns value without mask characters
}

export const MaskedInput: React.FC<MaskedInputProps> = ({
  label,
  error,
  maskType = "custom",
  customMask,
  variant = "default",
  containerStyle,
  inputStyle,
  disabled = false,
  helperText,
  onChangeUnmasked,
  ...props
}) => {
  const backgroundColor = useThemeColor({}, "backgroundTertiary");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const placeholderColor = useThemeColor({}, "textTertiary");
  const errorColor = useThemeColor({}, "error");
  const disabledTextColor = useThemeColor({}, "textDisabled");
  const disabledBackgroundColor = useThemeColor({}, "backgroundDisabled");

  const [maskedValue, setMaskedValue] = useState("");

  const getMask = (): string => {
    switch (maskType) {
      case "phone":
        return "(###) ###-####";
      case "credit-card":
        return "#### #### #### ####";
      case "date":
        return "##/##/####";
      case "time":
        return "##:##";
      case "zip-code":
        return "#####";
      case "ssn":
        return "###-##-####";
      case "custom":
        return customMask || "";
      default:
        return "";
    }
  };

  const mask = getMask();

  const applyMask = (value: string): string => {
    if (!mask) return value;

    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");

    let maskedResult = "";
    let digitIndex = 0;

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      if (mask[i] === "#") {
        maskedResult += digits[digitIndex];
        digitIndex++;
      } else {
        maskedResult += mask[i];
      }
    }

    return maskedResult;
  };

  const removeMask = (value: string): string => {
    return value.replace(/\D/g, "");
  };

  const handleChangeText = (text: string) => {
    const masked = applyMask(text);
    const unmasked = removeMask(text);

    setMaskedValue(masked);
    props.onChangeText?.(masked);
    onChangeUnmasked?.(unmasked);
  };

  const getKeyboardType = (): RNTextInputProps["keyboardType"] => {
    if (props.keyboardType) return props.keyboardType;

    switch (maskType) {
      case "phone":
      case "credit-card":
      case "zip-code":
      case "ssn":
        return "number-pad";
      case "date":
      case "time":
        return "numeric";
      default:
        return "default";
    }
  };

  const getPlaceholder = (): string => {
    if (props.placeholder) return props.placeholder;

    switch (maskType) {
      case "phone":
        return "(123) 456-7890";
      case "credit-card":
        return "1234 5678 9012 3456";
      case "date":
        return "MM/DD/YYYY";
      case "time":
        return "12:30";
      case "zip-code":
        return "12345";
      case "ssn":
        return "123-45-6789";
      default:
        return "";
    }
  };

  const getVariantStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      position: "relative",
      backgroundColor,
    };

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: disabled ? disabledBackgroundColor : backgroundColor,
        };
      case "outlined":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderWidth: 1,
          borderColor: error ? errorColor : borderColor,
        };
      default:
        return baseStyle;
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {label && (
        <ThemedText
          style={[styles.label, disabled && { color: disabledTextColor }]}
        >
          {label}
        </ThemedText>
      )}

      <View style={[getVariantStyle(), disabled && styles.disabled]}>
        <RNTextInput
          {...props}
          style={[
            styles.input,
            {
              color: disabled ? disabledTextColor : textColor,
            },
            inputStyle,
          ]}
          value={maskedValue || props.value?.toString() || ""}
          onChangeText={handleChangeText}
          placeholderTextColor={placeholderColor}
          placeholder={getPlaceholder()}
          keyboardType={getKeyboardType()}
          editable={!disabled}
          autoCorrect={false}
        />
      </View>

      {error && (
        <ThemedText style={[styles.error, { color: errorColor }]}>
          {error}
        </ThemedText>
      )}

      {helperText && !error && (
        <ThemedText style={[styles.helperText, { color: placeholderColor }]}>
          {helperText}
        </ThemedText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 4,
    fontSize: 14,
    fontFamily: fonts.medium,
  },
  input: {
    height: 50,
    fontSize: 16,
    padding: 14,
    fontFamily: fonts.regular,
  },
  disabled: {
    opacity: 0.5,
  },
  error: {
    marginTop: 4,
    fontSize: 12,
    fontFamily: fonts.regular,
  },
  helperText: {
    marginTop: 4,
    fontSize: 12,
    fontFamily: fonts.regular,
  },
});

export default MaskedInput;
